// ./src/utils/api/UserInvitationService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { UserRole } from "./UserRoleService";

// GraphQL queries
const USER_INVITATION_LIST_QUERY = gql`
  query UserInvitationList(
    $filter: UserInvitationFilter
    $params: PaginationAndSorting
  ) {
    userInvitationList(filter: $filter, params: $params) {
      code
      data {
        id
        firstName
        lastName
        email
        userRoleId
        expiresAt
        userRole {
          roleName
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_INVITATION_GET_QUERY = gql`
  query UserInvitationGet($id: ID) {
    userInvitationGet(id: $id) {
      code
      data {
        id
        firstName
        lastName
        email
        userRoleId
        expiresAt
        userRole {
          roleName
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_INVITATION_GET_MANY_QUERY = gql`
  query UserInvitationGetMany($ids: [ID]) {
    userInvitationGetMany(ids: $ids) {
      code
      data {
        id
        firstName
        lastName
        email
        userRoleId
        expiresAt
        userRole {
          roleName
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const USER_INVITATION_CREATE_MUTATION = gql`
  mutation UserInvitationCreate($params: UserInvitationInput) {
    userInvitationCreate(params: $params) {
      code
      data {
        id
        firstName
        lastName
        email
        userRoleId
        expiresAt
        userRole {
          roleName
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_INVITATION_RESEND_MUTATION = gql`
  mutation UserInvitationResend($id: ID) {
    userInvitationResend(id: $id) {
      code
      data {
        id
        firstName
        lastName
        email
        userRoleId
        expiresAt
        userRole {
          roleName
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_INVITATION_ACCEPT_MUTATION = gql`
  mutation UserInvitationResend($params: UserInvitationAcceptInput) {
    userInvitationAccept(params: $params) {
      code
      data {
        username
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_INVITATION_UPDATE_MUTATION = gql`
  mutation UserInvitationUpdate(
    $where: UserInvitationWhereInput
    $params: UserInvitationInput
  ) {
    userInvitationUpdate(where: $where, params: $params) {
      code
      data {
        id
        firstName
        lastName
        email
        expiresAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_INVITATION_REMOVE_MUTATION = gql`
  mutation UserInvitationRemove($where: UserInvitationWhereInput) {
    userInvitationRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_INVITATION_REMOVE_MANY_MUTATION = gql`
  mutation UserInvitationRemoveMany($where: [UserInvitationWhereInput]) {
    userInvitationRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface UserInvitation {
  id?: string | undefined;
  userRoleId?: string | null | undefined;
  firstName?: string | undefined;
  lastName?: string | undefined;
  email?: string | undefined;
  expiresAt?: string | undefined;
  userRole?: UserRole | null | undefined;
}

export interface UserInvitationInput {
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  email?: string | null | undefined;
  userRoleId?: string | null | undefined;
}

export interface UserInvitationAcceptInput {
  id?: string | null | undefined;
  username?: string | null | undefined;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  email?: string | null | undefined;
  phone?: string | null | undefined;
  password?: string | null | undefined;
  repeatPassword?: string | null | undefined;
}

export interface UserInvitationFilter {
  id?: string[] | null | undefined;
  email?: string | null | undefined;
  searchIn?: string[] | null | undefined;
  keyword?: string | null | undefined;
  userRoleId?: string | null | undefined;
}

export interface UserInvitationWhereInput {
  id?: string | undefined;
  username?: string | undefined;
}

/**
 * User Invitation service that handles all user invitation-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany, resend
 */
export class UserInvitationService {
  /**
   * Get a list of user invitations with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: UserInvitationFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(USER_INVITATION_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single user invitation by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(USER_INVITATION_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple user invitations by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      USER_INVITATION_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new user invitation
   */
  static async create(params: UserInvitationInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      USER_INVITATION_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Resend an existing user invitation
   */
  static async resend(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      USER_INVITATION_RESEND_MUTATION,
      {
        id,
      }
    );
    return result;
  }

  /**
   * Resend an existing user invitation
   */
  static async acceptInvitation(
    params: UserInvitationAcceptInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      USER_INVITATION_ACCEPT_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing user invitation
   */
  static async update(
    where: UserInvitationWhereInput,
    params: UserInvitationInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      USER_INVITATION_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a user invitation
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      USER_INVITATION_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple user invitations
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id: [id] }));
    const result = await ApiService.executeMutation(
      USER_INVITATION_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
